/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import { AGENT_MODEL, IDeviceData } from '../../providers/device/device';
import { EntityCacheProvider } from '../../providers/inputs.datastore/types';
import { DevicesService } from '../../providers/device/devices.service';
import { ITestQueueStatus, WarningMessage } from './types';
import { TimeoutWrapperService } from '../../providers/timeout.wrapper/timeout.wrapper.service';
import { BehaviorSubject } from 'rxjs';
import { UserService } from '../../providers/user/user.service';
import { TestTypeValue } from '../test-list/types';
import { REFLECTOR_TYPE, testConfigs } from './testsConfig';

@Injectable({
  providedIn: 'root'
})
export class ActiveTestQueuedStateService {
  deviceWithTests = [];
  deviceAssociatedTests = {};
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  usedTestCountCache = new BehaviorSubject<any>([]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  constructor(
    private readonly entityCache: EntityCacheProvider,
    private readonly deviceService: DevicesService,
    private readonly timeoutWrapperService: TimeoutWrapperService,
    private readonly userService: UserService
  ) {
    this.onInit();
  }

  onInit(): void {
    this.usedTestCountCache.next(null);
    this.deviceService.cache.subscribe(data => {
      if (data?.length) {
        if (this.userService.isFusionUIProject()) {
          this.getDevicesWithTests(true);
        }
      }
    });
  }

  // eslint-disable-next-line
  getDevicesWithTests(cacheQuery = false) {
    if (!cacheQuery && this.deviceWithTests.length) {
      return this.deviceWithTests;
    }
    let deviceList = [];
    let unmanagedList = [];
    this.entityCache
      .findEntityCache('testlist')
      .getValuesAsync()
      .subscribe(testListCache => {
        this.timeoutWrapperService.add(() => {
          deviceList = this.entityCache.findEntityCache('device')?.getValues();
          unmanagedList = this.entityCache.findEntityCache('unmanaged-devices')?.getValues();
          this.getDeviceAssociatedTests(testListCache);
          for (const element of deviceList) {
            const testQueueStatus = this.getDeviceTestAssignedStatus(
              testListCache,
              element.getDevice()
            );
            element.queueStatus = testQueueStatus;
          }
          this.deviceWithTests = [...deviceList, ...unmanagedList];
          this.usedTestCountCache.next(this.deviceWithTests);
        }, 0);
      });

    return this.deviceWithTests;
  }

  //eslint-disable-next-line
  getDeviceAssociatedTests(testCache: any): void {
    this.deviceAssociatedTests = {};
    for (const testData of testCache) {
      const deviceId = testData.getOriginatorAgentId();
      const destAgentId = testData.getDestinationAgentId();
      if (this.deviceAssociatedTests[deviceId]) {
        this.deviceAssociatedTests[deviceId].push(testData);
      } else {
        this.deviceAssociatedTests[deviceId] = [testData];
      }
      if (
        destAgentId &&
        [
          TestTypeValue.VOIP,
          TestTypeValue.TRUESPEED,
          TestTypeValue.Y1564,
          TestTypeValue.RFC2544,
          TestTypeValue.HTTP,
          TestTypeValue.FTP,
          TestTypeValue.UDP,
          TestTypeValue.BERT
        ].includes(testData.getTestType())
      ) {
        if (this.deviceAssociatedTests[destAgentId]) {
          this.deviceAssociatedTests[destAgentId].push(testData);
        } else {
          this.deviceAssociatedTests[destAgentId] = [testData];
        }
      }
    }
  }

  //eslint-disable-next-line
  getDeviceTestAssignedStatus(testCache, deviceData: IDeviceData): any {
    const testQueueStatus: ITestQueueStatus = {
      truespeed: { total: 0, used: 0, percentage: 0 },
      y1564: { total: 0, used: 0, percentage: 0 },
      bert: { total: 0, used: 0, percentage: 0 },
      y1731: { total: 0, used: 0, percentage: 0 },
      twamp: { total: 0, used: 0, percentage: 0 },
      rfc2544: { total: 0, used: 0, percentage: 0 },
      service: { total: 0, used: 0, percentage: 0 },
      voip: { total: 0, used: 0, percentage: 0 },
      multistep: { total: 0, used: 0, percentage: 0 },
      [REFLECTOR_TYPE.FULL]: { total: 0, used: 0, percentage: 0 },
      [REFLECTOR_TYPE.LIGHT]: { total: 0, used: 0, percentage: 0 },
      udp: { total: 0, used: 0, percentage: 0 },
      http: { total: 0, used: 0, percentage: 0 },
      ftp: { total: 0, used: 0, percentage: 0 },
      maxCount: 0,
      overall_percentage: 0,
      overall_total_used: 0
    };
    const tests = this.deviceAssociatedTests[deviceData.id];

    //default max test when "max-concurrent-test" not available
    const defaultMaxTest = deviceData.model?.model?.includes('JMEP')
      ? 1
      : deviceData.model?.model?.includes('QT')
      ? 30
      : 10;

    //MV-25649 - show reflector count only for vpma and vspa-pm device
    if (
      deviceData.model?.model === AGENT_MODEL.VPMA ||
      deviceData.model?.model === AGENT_MODEL.VPMA_L ||
      deviceData.model?.model === AGENT_MODEL.VSPA_PM ||
      deviceData.model?.model === AGENT_MODEL.VSPA_PM_L
    ) {
      testQueueStatus.maxCount =
        deviceData.capabilities.properties['max-concurrent-tests'] || defaultMaxTest;
    } else {
      //Getting max test count exluding reflectors count
      if (deviceData.phyId) {
        testQueueStatus.maxCount =
          deviceData.capabilities &&
          deviceData.capabilities.properties &&
          deviceData.capabilities.properties['max-concurrent-tests'] &&
          deviceData.capabilities['twamp-reflector-list']?.length
            ? deviceData.capabilities.properties['max-concurrent-tests'] -
              deviceData.capabilities['twamp-reflector-list'].reduce(
                (accumulator, current) => accumulator + parseInt(current['max-concurrent-tests']),
                0
              )
            : deviceData.capabilities
            ? //keeping default max-concurrent-tests for QT as some device not returning this value.
              deviceData.capabilities.properties?.['max-concurrent-tests'] || defaultMaxTest
            : defaultMaxTest;
      } else {
        testQueueStatus.maxCount = deviceData.capabilities?.properties?.['max-concurrent-tests']
          ? deviceData.capabilities['twamp-reflector']?.['max-concurrent-tests']
            ? deviceData.capabilities.properties['max-concurrent-tests'] -
              deviceData.capabilities['twamp-reflector']['max-concurrent-tests']
            : deviceData.capabilities['twamp-light-reflector']?.['max-concurrent-tests']
            ? deviceData.capabilities.properties['max-concurrent-tests'] -
              deviceData.capabilities['twamp-light-reflector']['max-concurrent-tests']
            : //keeping default max-concurrent-tests as some device not returning this value.
              deviceData.capabilities.properties?.['max-concurrent-tests'] || defaultMaxTest
          : defaultMaxTest;

        // some agents not included with reflector count like(vpma) and becomes "0" which might not allow to create test.
        testQueueStatus.maxCount =
          testQueueStatus.maxCount <= 0
            ? deviceData.capabilities.properties['max-concurrent-tests']
            : testQueueStatus.maxCount;
      }
    }

    // testQueueStatus.maxCount =
    //   deviceData.capabilities && deviceData.capabilities.properties
    //     ? deviceData.capabilities.properties['max-concurrent-tests'] || defaultMaxTest
    //     : defaultMaxTest;

    //logics for calculate test counts
    testConfigs.forEach(testConfig => {
      //MV-25649 - show reflector count only for vpma and vspa-pm device
      if (
        (testConfig.testType === TestTypeValue.TWAMP_REFLECTOR ||
          testConfig.testType === TestTypeValue.TWAMP_LIGHT_REFLECTOR) &&
        deviceData.model?.model !== AGENT_MODEL.VPMA &&
        deviceData.model?.model !== AGENT_MODEL.VPMA_L &&
        deviceData.model?.model !== AGENT_MODEL.VSPA_PM &&
        deviceData.model?.model !== AGENT_MODEL.VSPA_PM_L
      ) {
        return;
      }
      if (deviceData.phyId) {
        const capabilitiesList = deviceData.capabilities?.[testConfig.capabilities + '-list'];
        if (capabilitiesList) {
          const portItem = capabilitiesList.find(data => data['phy-id'] === deviceData.phyId);
          testQueueStatus[testConfig.testQueueStatusName].total = portItem
            ? portItem[testConfig.maxconcurrent]
            : undefined;
        } else {
          testQueueStatus[testConfig.testQueueStatusName].total = undefined;
        }
      } else {
        testQueueStatus[testConfig.testQueueStatusName].total =
          deviceData.capabilities &&
          deviceData.capabilities[testConfig.capabilities] &&
          deviceData.capabilities[testConfig.capabilities][testConfig.maxconcurrent]
            ? deviceData.capabilities[testConfig.capabilities][testConfig.maxconcurrent]
            : undefined;
      }

      testQueueStatus[testConfig.testQueueStatusName].used = tests
        ? tests.filter(
            test =>
              test.getTestStatus() == testConfig.operationalState &&
              test.getTestType() == testConfig.testType
          ).length
        : 0;

      testQueueStatus[testConfig.testQueueStatusName].percentage = testQueueStatus[
        testConfig.testQueueStatusName
      ].total
        ? (100 * testQueueStatus[testConfig.testQueueStatusName].used) /
          testQueueStatus[testConfig.testQueueStatusName].total
        : undefined;
    });
    // Overall percentage
    testQueueStatus.overall_percentage =
      (100 *
        (testQueueStatus.service.used +
          testQueueStatus.twamp.used +
          testQueueStatus.voip.used +
          testQueueStatus.truespeed.used +
          testQueueStatus.multistep.used +
          testQueueStatus.y1564.used +
          testQueueStatus.bert.used +
          testQueueStatus.y1731.used +
          testQueueStatus.rfc2544.used +
          testQueueStatus.udp.used +
          testQueueStatus.http.used +
          testQueueStatus.ftp.used +
          testQueueStatus[REFLECTOR_TYPE.FULL].used +
          testQueueStatus[REFLECTOR_TYPE.LIGHT].used)) /
      testQueueStatus.maxCount;
    testQueueStatus.overall_percentage = testQueueStatus.overall_percentage
      ? Math.round(testQueueStatus.overall_percentage)
      : testQueueStatus.overall_percentage;
    //Overall Used
    testQueueStatus.overall_total_used =
      testQueueStatus.service.used +
      testQueueStatus.twamp.used +
      testQueueStatus.voip.used +
      testQueueStatus.truespeed.used +
      testQueueStatus.multistep.used +
      testQueueStatus.y1564.used +
      testQueueStatus.bert.used +
      testQueueStatus.y1731.used +
      testQueueStatus.rfc2544.used +
      testQueueStatus.udp.used +
      testQueueStatus.http.used +
      testQueueStatus.ftp.used +
      testQueueStatus[REFLECTOR_TYPE.FULL].used +
      testQueueStatus[REFLECTOR_TYPE.LIGHT].used;
    return testQueueStatus;
  }

  checkAgentsAvailable(queueStatusConfig: ITestQueueStatus, currentTest: string): string {
    let warningMessages = WarningMessage.RESOURCE_AVAILABLE_WARNING_MESSAGE;
    switch (currentTest) {
      case TestTypeValue.TWAMP: {
        queueStatusConfig.twamp.total = queueStatusConfig.twamp.total
          ? queueStatusConfig.twamp.total
          : queueStatusConfig.maxCount;
        if (queueStatusConfig.twamp.used >= queueStatusConfig.twamp.total) {
          warningMessages = WarningMessage.TWAMP_WARNING_MESSAGE;
        }
        break;
      }
      case TestTypeValue.SERVICE_CONTINUITY: {
        queueStatusConfig.service.total = queueStatusConfig.service.total
          ? queueStatusConfig.service.total
          : queueStatusConfig.maxCount;
        if (queueStatusConfig.service.used >= queueStatusConfig.service.total) {
          warningMessages = WarningMessage.SERVICE_WARNING_MESSAGE;
        }
        break;
      }
      case TestTypeValue.VOIP: {
        queueStatusConfig.voip.total = queueStatusConfig.voip.total
          ? queueStatusConfig.voip.total
          : queueStatusConfig.maxCount;
        if (queueStatusConfig.voip.used >= queueStatusConfig.voip.total) {
          warningMessages = WarningMessage.VOIP_WARNING_MESSAGE;
        }
        break;
      }
      case TestTypeValue.TRUESPEED: {
        queueStatusConfig.truespeed.total = queueStatusConfig.truespeed.total
          ? queueStatusConfig.truespeed.total
          : queueStatusConfig.maxCount;
        if (queueStatusConfig.truespeed.used >= queueStatusConfig.truespeed.total) {
          warningMessages = WarningMessage.TRUESPEED_WARNING_MESSAGE;
        }
        break;
      }
      case TestTypeValue.MULTISTEP: {
        queueStatusConfig.multistep.total = queueStatusConfig.multistep.total
          ? queueStatusConfig.multistep.total
          : 0;
        if (queueStatusConfig.multistep.used >= queueStatusConfig.multistep.total) {
          warningMessages = WarningMessage.MULTISTEP_WARNING_MESSAGE;
        }
        break;
      }
      case TestTypeValue.Y1564: {
        queueStatusConfig.y1564.total = queueStatusConfig.y1564.total
          ? queueStatusConfig.y1564.total
          : queueStatusConfig.maxCount;
        if (queueStatusConfig.y1564.used >= queueStatusConfig.y1564.total) {
          warningMessages = WarningMessage.Y1564_WARNING_MESSAGE;
        }
        break;
      }
      case TestTypeValue.BERT: {
        queueStatusConfig.bert.total = queueStatusConfig.bert.total
          ? queueStatusConfig.bert.total
          : queueStatusConfig.maxCount;
        if (queueStatusConfig.bert.used >= queueStatusConfig.bert.total) {
          warningMessages = WarningMessage.BERT_WARNING_MESSAGE;
        }
        break;
      }
      case TestTypeValue.Y1731: {
        queueStatusConfig.y1731.total = queueStatusConfig.y1731.total
          ? queueStatusConfig.y1731.total
          : queueStatusConfig.maxCount;
        if (queueStatusConfig.y1731.used >= queueStatusConfig.y1731.total) {
          warningMessages = WarningMessage.Y1731_WARNING_MESSAGE;
        }
        break;
      }
      case TestTypeValue.RFC2544: {
        queueStatusConfig.rfc2544.total = queueStatusConfig.rfc2544.total
          ? queueStatusConfig.rfc2544.total
          : queueStatusConfig.maxCount;
        if (queueStatusConfig.rfc2544.used >= queueStatusConfig.rfc2544.total) {
          warningMessages = WarningMessage.RFC2544_WARNING_MESSAGE;
        }
        break;
      }
      case TestTypeValue.UDP: {
        queueStatusConfig.udp.total = queueStatusConfig.udp.total
          ? queueStatusConfig.udp.total
          : queueStatusConfig.maxCount;
        if (queueStatusConfig.udp.used >= queueStatusConfig.udp.total) {
          warningMessages = WarningMessage.UDP_WARNING_MESSAGE;
        }
        break;
      }
      case TestTypeValue.HTTP: {
        queueStatusConfig.http.total = queueStatusConfig.http.total
          ? queueStatusConfig.http.total
          : queueStatusConfig.maxCount;
        if (queueStatusConfig.http.used >= queueStatusConfig.http.total) {
          warningMessages = WarningMessage.HTTP_WARNING_MESSAGE;
        }
        break;
      }
      case TestTypeValue.FTP: {
        queueStatusConfig.ftp.total = queueStatusConfig.ftp.total
          ? queueStatusConfig.ftp.total
          : queueStatusConfig.maxCount;
        if (queueStatusConfig.ftp.used >= queueStatusConfig.ftp.total) {
          warningMessages = WarningMessage.FTP_WARNING_MESSAGE;
        }
        break;
      }
    }
    return warningMessages;
  }

  warningMessage(agentId: string, selectedTest: string): string {
    let warningMessages: string;
    if (agentId) {
      const devices = this.getDevicesWithTests().filter(device => device.deviceData.id == agentId);
      const queueStatusConfig = devices?.[0]?.queueStatus;
      // Invidiual agent warning message takes precedence then overall warning message
      if (queueStatusConfig) {
        warningMessages = this.checkAgentsAvailable(queueStatusConfig, selectedTest);
        if (warningMessages === WarningMessage.RESOURCE_AVAILABLE_WARNING_MESSAGE) {
          if (queueStatusConfig.overall_total_used >= queueStatusConfig.maxCount) {
            warningMessages = WarningMessage.ALL_TEST_WARNING_MESSAGE;
          }
        }
      }
    }
    return warningMessages;
  }

  capitalizeFirstLetter(word: string): string {
    if (!word) return word;
    return word[0].toUpperCase() + word.substr(1).toLowerCase();
  }
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  getMoreInformation(deviceInformation: any): string {
    let formatString = '';
    for (const [key, value] of Object.entries(deviceInformation.queueStatus)) {
      if (typeof value === 'object' && value['total']) {
        formatString =
          formatString +
          `${this.capitalizeFirstLetter(key)} ${value['used']} of ${value['total']} used \n \n`;
      }
    }
    return `Breakout by test type \n \n` + formatString;
  }
}
